import {
    ECpmCReferenceExpressionSourceTypeApiEnum,
    RECpmCReferenceExpressionSourceTypeApiEnum
} from '../../../gen/cpm-c-api-ts/src/enums/c-global/cpmc-reference-expression.api-enums';

export class ReferenceExpressionInstance {
    constructor(
        public start: number,
        public end: number,
        public attributeName: string,
        public sourceType: ECpmCReferenceExpressionSourceTypeApiEnum,
        public sourceIdentifier: string
    ) {}
}

export abstract class ReferenceExpressionUtils {
    public static readonly EXPRESSION_PREFIX = '${';
    public static readonly EXPRESSION_SUFFIX = '}';
    public static readonly EXPRESSION_SEPARATOR = '@';

    public static readonly EXPRESSION_ATTRIBUTE_NAME_REGEX = /^[A-Za-z0-9_]+$/;
    public static readonly EXPRESSION_SOURCE_TYPE_REGEX = /^[A-Za-z0-9]+$/;
    public static readonly EXPRESSION_SOURCE_IDENTIFIER_REGEX = /^[A-Za-z0-9_-]+$/;

    public static readonly EXPRESSION_REGEX =
        /\${(?<attributeName>[A-Za-z0-9_]+)@(?<sourceTypeStr>[A-Za-z0-9]+):(?<sourceIdentifier>[A-Za-z0-9_-]+)}/g;

    // ===============================================================
    // ===============================================================

    /**
     * Build an expression
     * @param attributeName Name of the attribute
     * @param sourceType Source type
     * @param sourceIdentifier Source identifier
     * @returns Built expression
     */
    public static buildExpression(
        attributeName: string,
        sourceType: ECpmCReferenceExpressionSourceTypeApiEnum,
        sourceIdentifier: string
    ) {
        if (!ReferenceExpressionUtils.isAttributeNameValid(attributeName))
            throw new Error(`Invalid reference expression attribute name '${attributeName}'`);

        if (!ReferenceExpressionUtils.isSourceTypeValid(sourceType))
            throw new Error(`Invalid reference expression source type '${sourceType}'`);

        if (!ReferenceExpressionUtils.isSourceIdentifierValid(sourceIdentifier))
            throw new Error(`Invalid reference expression source identifier '${sourceIdentifier}'`);

        return `$\{${attributeName}@${sourceType}:${sourceIdentifier}}`;
    }
    /**
     * Finds every `${attributeName@sourceType:sourceIdentifier}` in the
     * supplied text and returns a list of {@link ReferenceExpressionInstance}s.
     *
     * The method will **ignore** fragments that fail validation (you could
     * change this to throw instead – see the comment inside the loop).
     */
    public static parseExpressions(from: string, skipInvalids = false): ReferenceExpressionInstance[] {
        const results: ReferenceExpressionInstance[] = [];

        // Use the global regex – exec will advance automatically
        let match: RegExpExecArray | null;

        ReferenceExpressionUtils.EXPRESSION_REGEX.lastIndex = 0;
        while ((match = ReferenceExpressionUtils.EXPRESSION_REGEX.exec(from)) !== null) {
            // Destructure the named capture groups
            const {attributeName, sourceTypeStr, sourceIdentifier} = match.groups as {
                attributeName: string;
                sourceTypeStr: string;
                sourceIdentifier: string;
            };

            // Basic validation (the regex already guarantees the format,
            // but we double‑check to be safe)
            if (
                !ReferenceExpressionUtils.isAttributeNameValid(attributeName) ||
                !ReferenceExpressionUtils.isSourceIdentifierValid(sourceIdentifier)
            ) {
                if (!skipInvalids) throw new Error(`Invalid expression: ${match[0]}`);
                continue;
            }

            // Convert the string value to the enum; if it doesn't exist,
            // skip/throw as well.
            const sourceType = sourceTypeStr as ECpmCReferenceExpressionSourceTypeApiEnum;
            if (!ReferenceExpressionUtils.isSourceTypeValid(sourceType)) {
                if (!skipInvalids) throw new Error(`Unknown source type '${sourceTypeStr}'`);

                continue;
            }

            // Create the instance – the indices are based on the match
            results.push(
                new ReferenceExpressionInstance(
                    match.index, // start
                    match.index + match[0].length, // end
                    attributeName,
                    sourceType,
                    sourceIdentifier
                )
            );
        }

        return results;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Test if an attribute name format is valid
     * @param attributeName Attribute name to test
     * @returns True if valid
     */
    public static isAttributeNameValid(attributeName: string): boolean {
        ReferenceExpressionUtils.EXPRESSION_ATTRIBUTE_NAME_REGEX.lastIndex = 0;
        return ReferenceExpressionUtils.EXPRESSION_ATTRIBUTE_NAME_REGEX.test(attributeName);
    }
    /**
     * Test if a source type is valid
     * @param sourceType Source type to test
     * @returns True if valid
     */
    public static isSourceTypeValid(sourceType: ECpmCReferenceExpressionSourceTypeApiEnum): boolean {
        return sourceType in RECpmCReferenceExpressionSourceTypeApiEnum;
    }
    /**
     * Test if a source identifier format is valid
     * @param sourceIdentifier Source identifier to test
     * @returns True if valid
     */
    public static isSourceIdentifierValid(sourceIdentifier: string): boolean {
        ReferenceExpressionUtils.EXPRESSION_SOURCE_IDENTIFIER_REGEX.lastIndex = 0;
        return ReferenceExpressionUtils.EXPRESSION_SOURCE_IDENTIFIER_REGEX.test(sourceIdentifier);
    }
}
